import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import { Table } from 'primeng/table';
import {Router} from '@angular/router';
import {ConfiguratorPageHtml5Service, DatastoreDto, PageHtml5DTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, LazyLoadEvent, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';

@Component({
    templateUrl: './template-html.component.html',
    styleUrls: ['./template-html.component.scss'],
    standalone: false
})
export class TemplateHtmlComponent implements OnInit {
    breadcrumb = [
        {label: this.translate.instant('menu.landingpage.label')},
        {label: this.translate.instant('menu.landingpage.html-template')}
    ];
    description = this.translate.instant('landingPage.template.description');

    datastore: DatastoreDto;

    templates = [];
    totalRecords = 0;
    selectedTemplates: PageHtml5DTO[] = [];
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];

    actualEvent: LazyLoadEvent = {
        first: 0,
        rows: 25
    };

    state: { message: string };

    @ViewChild('templateFileInput') templateFileInput: ElementRef;

    constructor(private templateHtmlService: ConfiguratorPageHtml5Service, private confirmationService: ConfirmationService,
                private messageService: MessageService, private router: Router, private readonly translate: TranslateService) {
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state as { message: string } | undefined;
        this.state = state;
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.landingpage.label')},
            {label: this.translate.instant('menu.landingpage.html-template')}
        ];
        this.description = this.translate.instant('landingPage.template.description');
    }

    ngOnInit(): void {
        this.initExportMenu();
        if (this.state?.message === 'success_created') {
            setTimeout(() => this.messageService.add({severity: 'success', summary: this.translate.instant('datastore.successSummary'), detail: this.translate.instant('landingPage.template.userCreatedDetail')}));
        } else if (this.state?.message === 'success_updated') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('datastore.successSummary'),
                detail: this.translate.instant('landingPage.template.userUpdatedDetail')
            }));
        }
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        this.loadLazy();
    }

    @ViewChild(Table) table: Table;
    search = '';

    onSearch(value: string) {
        this.search = value;
        this.table ? this.table.reset() : this.loadLazy();
    }

    loadLazy(event: LazyLoadEvent = null) {
        if (event) {
            this.actualEvent = event;
        }
        const start = Math.round(this.actualEvent.first / this.actualEvent.rows);
        const end = this.actualEvent.rows;
        this.templateHtmlService.getAllPageHtml5SUsingGET(this.datastore?.code, start, end, [], this.search, 'response').subscribe((res) => {
            this.totalRecords = +res.headers.get('x-total-count');
            this.templates = res.body;
            this.updateExportMenuState();
        });
    }

    remove(template: PageHtml5DTO) {
        this.confirmationService.confirm({
            message: this.translate.instant('landingPage.template.confirmDeleteMessage', {name: template.name}),
            accept: () => {
                this.templateHtmlService.deletePageHtml5UsingDELETE(this.datastore.code, template.id).subscribe(() => {
                    this.loadLazy();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('landingPage.template.deleteSuccessDetail')
                    });
                }, () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('landingPage.template.genericErrorDetail')
                    });
                });
            }
        });
    }

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedTemplates(),
                disabled: !this.selectedTemplates || this.selectedTemplates.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllTemplates(),
                disabled: this.totalRecords === 0
            }
        ];
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.templateFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.templateHtmlService.importPageHtml5Json(this.datastore.code, jsonContent).subscribe(
                (template: PageHtml5DTO) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('landingPage.template.importSuccessDetail', {name: template.name})
                    });
                    this.loadLazy();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.templateHtmlService.importPageHtml5Zip(this.datastore.code, file).subscribe(
            (templates: PageHtml5DTO[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('landingPage.template.importSuccessMultipleDetail', {count: templates.length})
                });
                this.loadLazy();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte un seul template en JSON
     */
    exportTemplateJson(template: PageHtml5DTO) {
        this.templateHtmlService.exportPageHtml5Json(this.datastore.code, template.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `template-${template.code}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('landingPage.template.exportSuccessDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte plusieurs templates en ZIP
     */
    exportSelectedTemplates() {
        if (!this.selectedTemplates || this.selectedTemplates.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('landingPage.template.noTemplateSelectedDetail')
            });
            return;
        }

        const ids = this.selectedTemplates.map(t => t.id);
        this.templateHtmlService.exportPageHtml5Zip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `templates-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('landingPage.template.exportSuccessMultipleDetail', {count: this.selectedTemplates.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte tous les templates en ZIP
     */
    exportAllTemplates() {
        if (this.totalRecords === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('landingPage.template.noTemplateToExportDetail')
            });
            return;
        }

        // Récupérer TOUS les templates (sans pagination)
        this.templateHtmlService.getAllPageHtml5SUsingGET(this.datastore.code, 0, 10000, [], undefined, 'response').subscribe(
            (res) => {
                const allTemplates = res.body;
                const ids = allTemplates.map(t => t.id);

                this.templateHtmlService.exportPageHtml5Zip(this.datastore.code, ids).subscribe(
                    (blob: Blob) => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `templates-export-all.zip`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);

                        this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('datastore.successSummary'),
                            detail: this.translate.instant('landingPage.template.exportSuccessMultipleDetail', {count: allTemplates.length})
                        });
                    },
                    (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('datastore.errorSummary'),
                            detail: this.translate.instant('subscriptions.exportErrorDetail')
                        });
                    }
                );
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('landingPage.template.fetchErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedTemplates || this.selectedTemplates.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = this.totalRecords === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }
}
